/** Fitting a sheet viewport's frame around the floor's drawing (wall sketch, rooms, objects). */

import type { DxfEntity } from '@pointorama/dxf-exporter';
import { fitStandardScale, paperMmToWorld } from 'shared-types';

export interface PlanPoint {
  x: number;
  y: number;
}

/** Every vertex / anchor of the plan entities (what the frame must contain). */
export function planPoints(entities: readonly DxfEntity[]): PlanPoint[] {
  const out: PlanPoint[] = [];
  const add = (p: { x: number; y: number }) => Number.isFinite(p.x) && Number.isFinite(p.y) && out.push({ x: p.x, y: p.y });
  for (const entity of entities) {
    switch (entity.type) {
      case 'LINE':
        add(entity.start);
        add(entity.end);
        break;
      case 'POLYLINE':
        entity.points.forEach(add);
        break;
      case 'HATCH':
        entity.outerBoundary.forEach(add);
        break;
      case 'POINT':
      case 'TEXT':
      case 'INSERT':
        add(entity.position);
        break;
    }
  }
  return out;
}

/** Share of the viewport the fitted drawing may take (a little margin all round). */
const FIT_MARGIN = 0.92;

/**
 * The frame that holds all `points` at `headingDeg`: centered on their
 * extent as the page sees it (rotated), at the most detailed standard scale
 * that fits the viewport's paper size. null without points.
 */
export function frameAround(points: readonly PlanPoint[], headingDeg: number, paper: { wMm: number; hMm: number }): { center: PlanPoint; scaleDen: number } | null {
  if (points.length === 0) return null;
  const h = (headingDeg * Math.PI) / 180;
  const cos = Math.cos(h);
  const sin = Math.sin(h);
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  for (const p of points) {
    // Page right = (cos h, sin h), page up = (−sin h, cos h).
    const u = p.x * cos + p.y * sin;
    const v = -p.x * sin + p.y * cos;
    minU = Math.min(minU, u);
    maxU = Math.max(maxU, u);
    minV = Math.min(minV, v);
    maxV = Math.max(maxV, v);
  }
  const u = (minU + maxU) / 2;
  const v = (minV + maxV) / 2;
  const scaleDen = fitStandardScale(Math.max(maxU - minU, 0.01), Math.max(maxV - minV, 0.01), paper.wMm * FIT_MARGIN, paper.hMm * FIT_MARGIN);
  return { center: { x: u * cos - v * sin, y: u * sin + v * cos }, scaleDen };
}

/** The frame's world size at a scale. */
export function frameWorldSize(paper: { wMm: number; hMm: number }, scaleDen: number): { w: number; h: number } {
  return { w: paperMmToWorld(paper.wMm, scaleDen), h: paperMmToWorld(paper.hMm, scaleDen) };
}

/** The plan's straight pieces (lines, polyline edges, hatch outlines): what the dimension tool can measure in one click. */
export function planSegments(entities: readonly DxfEntity[]): [PlanPoint, PlanPoint][] {
  const out: [PlanPoint, PlanPoint][] = [];
  const ok = (p: { x: number; y: number }) => Number.isFinite(p.x) && Number.isFinite(p.y);
  const add = (a: { x: number; y: number }, b: { x: number; y: number }) => ok(a) && ok(b) && (a.x !== b.x || a.y !== b.y) && out.push([{ x: a.x, y: a.y }, { x: b.x, y: b.y }]);
  const ring = (points: readonly { x: number; y: number }[], closed: boolean, bulges?: readonly number[]) => {
    for (let i = 0; i + 1 < points.length; i++) if (!bulges?.[i]) add(points[i], points[i + 1]);
    if (closed && points.length > 2 && !bulges?.[points.length - 1]) add(points[points.length - 1], points[0]);
  };
  for (const entity of entities) {
    if (entity.type === 'LINE') add(entity.start, entity.end);
    else if (entity.type === 'POLYLINE') ring(entity.points, entity.closed, entity.bulges);
    else if (entity.type === 'HATCH') ring(entity.outerBoundary, true);
  }
  return out;
}

